import type { Inspiration, Character } from './crew';
import type { FeedPost } from './feed';
import type { Challenge, Work } from '../data/types';
import { getChallenge } from '../data/exercises';
import { SLOTS, REQUIRED, slotsFor, type Slot, type Pencilled } from '../data/film';

/**
 * Your pieces (K33, owner 2026-09-21): every exercise output is a piece, the
 * film's row holds one of each kind, and the rest is your collection.
 *
 * Only what is *yours* is a piece (owner, same day: "the film is only using
 * concepts that I came up with"). So: each idea you typed is its own card,
 * never the card's summary line ("2 ideas to change a wedding" is two pieces,
 * each one idea). The crew's finds count only by the exact words they quoted
 * from you, never by their paraphrase; when the quote is one of your typed
 * ideas, the crew's read just tells that idea which slot it fits. The crew's
 * drawings stay, captioned with your words (or the name you gave). Anything
 * with none of your words in it is left out, and a slot with nothing stays blank.
 *
 * And a piece may only sit in a slot its exercise gives (owner, 2026-09-22:
 * "characters should only come from dedicated character creation activities"):
 * a crew find or drawing that calls a Ten Bad Ideas line a person is dropped,
 * and the line stays what its exercise says it is.
 */
export type PieceSource = 'crew' | 'find' | 'render' | 'card';
export interface FilmPiece {
  /** stable across reloads: `${source}:${refId}` */
  id: string;
  refId: string;
  slot: Slot;
  /** your words: a typed idea, a name, a line */
  text: string;
  /** where it came from, for the caption */
  title: string;
  img?: string;
  /** the image is a card PNG, shown cropped to its sheet */
  sheet?: boolean;
  source: PieceSource;
  day?: number;
  at: number;
  postId?: string;
}

/**
 * What the profile keeps of a choice: enough for the backend to roll from it
 * without this device (kindling-film.service mergePicks). `sheet` says the
 * picture is a card PNG, headline and frame included: fine to show on the
 * table, never a first frame (activities audit §3.3: a photo card seated as
 * someone became the opening of a clip whose prompt says "No text").
 */
export interface PickRef { id: string; kind: PieceSource; refId: string; text: string; img?: string; sheet?: boolean; postId?: string }

const SLOT_OF_KIND: Record<string, Slot> = { character: 'someone', place: 'somewhere', premise: 'a want', style: 'a look', line: 'a line' };
export const SLOT_HUE: Record<Slot, 'coral' | 'green' | 'butter' | 'pink' | 'navy'> = { someone: 'coral', somewhere: 'green', 'a want': 'butter', 'a look': 'pink', 'a line': 'navy' };
export const SLOT_NOUN: Record<Slot, string> = { someone: 'someone', somewhere: 'somewhere', 'a want': 'a want', 'a look': 'a look', 'a line': 'a first line' };

const isSlot = (s: string): s is Slot => (SLOTS as readonly string[]).includes(s);
const norm = (s: string) => s.toLowerCase().replace(/[“”"'‘’.,;:!?()\s]+/g, ' ').trim();

/** One part of what you wrote, and the slot of the film it goes to. */
export interface Part { text: string; slot: Slot }

const clean = (s: string | undefined) => (s || '').replace(/\s+/g, ' ').trim();

/**
 * What you wrote, split into the parts the film can use, each with its slot
 * (activities audit, well/product/22 §3.1). It used to be `ideasIn`, which gave
 * every part to the first slot `gives` lists, so the order of `gives` silently
 * decided everything: Face in the Crowd's worry, which is the want, was joined
 * into the stranger's name and seated as Starring.
 *
 * Now an exercise can say where each part goes (`input.slots`: a field key, a
 * line index with "-1" for the last one written, "pick", "then:<key>"). A part
 * it does not name goes to the first slot, as before; so does one named for a
 * slot the exercise does not give (the owner's rule, 2026-09-22: a piece only
 * sits where its exercise says it can). "scrap", the word `gives` uses for
 * "fills nothing", keeps a part out of the film. Lines and pairs stay one part
 * each; fields that land in the same slot join into one (" · "), as fields
 * always did, and fields for different slots come apart. A closing field the
 * exercise did not route joins only its unrouted kin, never the routed answer:
 * Fifty Things' "what happens next?" must not bury the want it leads to.
 *
 * The picked line leads, then what the exercise routed on purpose, then the
 * rest: that order is the reel's order within a slot, and a closing answer
 * ("Who does that? Give them a name.") is the someone in the person's own words,
 * so it outranks nine unrouted lines. A letter is its lines, plus the sign-off
 * once it is not the one we wrote (it used to read `work.text`, which the letter
 * runner never sets, so Complaint Letter put nothing on the table).
 */
export function partsIn(c: Challenge, work: Work | undefined): Part[] {
  const gives = slotsFor(c);
  if (!work || !gives.length) return [];
  const spec = c.input;
  const routes = spec.slots || {};
  const route = (key: string): { slot: Slot; named: boolean } => {
    const s = routes[key];
    return s && isSlot(s) && gives.includes(s) ? { slot: s, named: true } : { slot: gives[0], named: false };
  };
  // `group`: parts with the same one join into a single piece; null never joins
  type Raw = Part & { rank: number; group: string | null };
  const raw: Raw[] = [];
  const add = (text: string, key: string, join: 'field' | 'then' | null, lead = false) => {
    if (!text || routes[key] === 'scrap') return;
    const r = route(key);
    const group = join === null ? null : `${join === 'then' && !r.named ? 'then' : 'field'}:${r.slot}`;
    raw.push({ text, slot: r.slot, rank: lead ? 0 : r.named ? 1 : 2, group });
  };
  const F = work.fields || {};
  const thenKeys = new Set((spec.then?.fields || []).map((f) => f.key));
  const listed = (keys: string[]) => keys.forEach((k) => add(clean(F[k]), k, 'field'));

  if (spec.kind === 'lines' || spec.kind === 'letter') {
    const lines = (work.lines || []).map(clean);
    const last = lines.map((l, i) => (l ? i : -1)).filter((i) => i >= 0).pop();
    const picked = spec.kind === 'lines' && work.pick !== undefined && lines[work.pick] ? work.pick : undefined;
    const keyOf = (i: number) => (routes[String(i)] ? String(i) : i === last && routes['-1'] ? '-1' : String(i));
    if (picked !== undefined) add(lines[picked], routes.pick ? 'pick' : keyOf(picked), null, true);
    lines.forEach((l, i) => { if (i !== picked) add(l, keyOf(i), null); });
    if (spec.kind === 'letter') { const so = clean(F.signoff); if (so && so !== clean(spec.signoff)) add(so, 'signoff', 'field'); }
  } else if (spec.kind === 'pairs') {
    (work.pairs || []).forEach((p, i) => add(clean(p.sub) ? `${clean(p.name)}: ${clean(p.sub)}` : clean(p.name), String(i), null));
  } else if (spec.kind === 'fields') {
    const keys = (spec.fields || []).map((f) => f.key);
    listed([...keys, ...Object.keys(F).filter((k) => !keys.includes(k) && !thenKeys.has(k))]);
  } else if (spec.kind === 'emoji') {
    add((work.emoji || []).map(clean).filter(Boolean).join(' '), 'emoji', null);
  } else if (spec.kind === 'text') {
    add(clean(work.text), 'text', null);
  }
  // the closing step, whatever the main input was
  for (const k of thenKeys) add(clean(F[k]), `then:${k}`, 'then');

  // fields bound for the same slot are one part, where the first of them stood
  const out: Raw[] = [];
  for (const r of raw) {
    const same = r.group ? out.find((o) => o.group === r.group) : undefined;
    if (same) { same.text = `${same.text} · ${r.text}`; same.rank = Math.min(same.rank, r.rank); } else out.push({ ...r });
  }
  return out.map((r, i) => ({ r, i })).sort((a, b) => a.r.rank - b.r.rank || a.i - b.i).map(({ r }) => ({ text: r.text, slot: r.slot }));
}

/** Where a card just made lands: the first of your parts whose slot the film has nothing in, else your first. Null when nothing you wrote feeds the film (never the card's headline: that is our copy, not yours). */
export function landingOf(parts: Part[], current: Partial<Record<Slot, FilmPiece>>): { part: Part; index: number } | null {
  if (!parts.length) return null;
  const i = parts.findIndex((p) => !current[p.slot]);
  return { part: parts[Math.max(0, i)], index: Math.max(0, i) };
}

/** The slot the crew's drawing of one of your ideas sits in: the slot of the part it drew, else the exercise's first. */
export function slotOfIdea(parts: Part[], idea: string | undefined, fallback: Slot): Slot {
  const q = norm(idea || '');
  return parts.find((p) => norm(p.text) === q)?.slot ?? fallback;
}

export function collectPieces(kept: Inspiration[], chars: Character[], posts: FeedPost[], pencilled?: Record<string, Pencilled>): FilmPiece[] {
  const out: FilmPiece[] = [];
  const postAt = new Map(posts.map((p) => [p.postId, p.createdAt]));

  // 1. your typed ideas, one card each; a photo or a recording is the card itself
  for (const p of posts) {
    if (p.invite || p.kind === 'film' || p.kind === 'invite' || p.kind === 'character') continue;
    const c = getChallenge(p.challengeId);
    if (!c) continue;
    const slots = slotsFor(c);
    if (!slots.length) continue;
    const parts = partsIn(c, p.work);
    parts.forEach((part, i) => out.push({ id: `card:${p.postId}:${i}`, refId: `${p.postId}:${i}`, postId: p.postId, slot: part.slot, text: part.text, title: `${c.title} · Day ${p.day}`, source: 'card', at: p.createdAt - i, day: p.day }));
    // a photograph or a recording is the card itself, in the first slot, beside any closing answer routed elsewhere
    if ((c.input.kind === 'photo' || c.input.kind === 'talk') && p.cardUrl && !p.cardUrl.startsWith('blob:')) {
      out.push({ id: `card:${p.postId}`, refId: p.postId, postId: p.postId, slot: slots[0], text: p.headline || c.title, title: `${c.title} · Day ${p.day}`, img: p.cardUrl, sheet: true, source: 'card', at: p.createdAt, day: p.day });
    }
    // the crew's picture of one of your ideas: your idea, drawn
    const r = (p.renders || []).find((x) => !!x.url && !!x.idea?.trim());
    // the crew's picture of one of your ideas is that idea with a picture, in the idea's slot (owner, 2026-09-22: a drawing of "bigger food items" is not a place)
    if (r) out.push({ id: `render:${p.postId}`, refId: p.postId, postId: p.postId, slot: slotOfIdea(parts, r.idea, slots[0]), text: r.idea.trim(), title: `Your Day ${p.day} idea · drawn by your crew`, img: r.url, source: 'render', at: p.createdAt + 1, day: p.day });
  }

  // what each post's exercise gives, so a find or drawing cannot make a person out of a list of ideas
  const givesOf = new Map<string, Slot[]>();
  for (const p of posts) { const c = getChallenge(p.challengeId); if (c) givesOf.set(p.postId, slotsFor(c)); }
  const allowed = (postId: string | undefined, slot: Slot) => { const g = postId ? givesOf.get(postId) : undefined; return !g || g.includes(slot); };

  // 2. the crew's finds: only by your exact words. When the quote is one of your typed ideas, it re-slots that idea instead.
  const drawn = new Set(chars.map((c) => c.id));
  for (const i of kept) {
    if (i.status !== 'kept') continue;
    const slot = isSlot(i.slot) ? i.slot : SLOT_OF_KIND[i.kind];
    const quote = (i.quote || '').replace(/\s+/g, ' ').trim();
    if (!slot || !quote) continue;
    if (!allowed(i.fromPostId, slot)) continue;
    if (i.renderedAs && drawn.has(i.renderedAs)) continue; // the drawing stands in for it (below)
    const q = norm(quote);
    const typed = out.find((p) => p.source === 'card' && !p.img && (norm(p.text) === q || (q.length > 12 && (norm(p.text).includes(q) || q.includes(norm(p.text))))));
    if (typed) { typed.slot = slot; continue; }
    out.push({ id: `find:${i.id}`, refId: i.id, slot, text: quote, title: `Your words · Day ${i.madeFor}`, source: 'find', at: postAt.get(i.fromPostId) || i.madeFor, postId: i.fromPostId, day: i.madeFor });
  }

  // a picture follows the idea it is of: when a find re-slotted the typed idea, the picture goes with it
  for (const r of out) {
    if (r.source !== 'render') continue;
    const typed = out.find((p) => p.source === 'card' && !p.img && p.postId === r.postId && norm(p.text) === norm(r.text));
    if (typed) r.slot = typed.slot;
  }

  // 3. the crew's drawings, captioned with the name you gave or the words they drew from
  // "Who are you?" is the one character exercise whose post gives no slot: its drawing is you, and you can always be the someone
  for (const c of chars) {
    const slot = SLOT_OF_KIND[c.kind];
    const words = (c.name || c.quote || (c.self ? 'You' : '')).replace(/\s+/g, ' ').trim();
    if (!slot || !c.imageUrl || !words || (!c.self && !allowed(c.fromPostId, slot))) continue;
    out.push({ id: `crew:${c.id}`, refId: c.id, slot, text: words, title: c.self ? 'You · drawn by your crew' : c.name ? `${c.name} · drawn by your crew` : 'Your words · drawn by your crew', img: c.imageUrl, source: 'crew', at: c.createdAt || postAt.get(c.fromPostId) || 0, postId: c.fromPostId });
  }

  // 4. offline only: what landed before the table existed, when the posts have not come
  if (posts.length === 0) {
    for (const [slot, pen] of Object.entries(pencilled || {})) {
      if (!isSlot(slot)) continue;
      const ref = pen.postId || `${pen.challengeId}:${pen.at}`;
      if (pen.pictureUrl) out.push({ id: `render:${ref}`, refId: ref, postId: pen.postId, slot, text: pen.headline, title: `${pen.title} · drawn by your crew`, img: pen.pictureUrl, source: 'render', at: pen.at });
    }
  }

  const byId = new Map<string, FilmPiece>();
  for (const p of out) if (!byId.has(p.id)) byId.set(p.id, p);
  return [...byId.values()].sort((a, b) => b.at - a.at);
}

/** The reel for one slot: the crew's drawings of your ideas first, then what you typed, newest first within each. */
const RANK: Record<PieceSource, number> = { crew: 0, render: 1, card: 2, find: 3 };
export function optionsFor(pieces: FilmPiece[], slot: Slot): FilmPiece[] {
  return pieces.filter((p) => p.slot === slot).sort((a, b) => RANK[a.source] - RANK[b.source] || b.at - a.at);
}

/** What is in the film now: the pick for each slot when it is still in the collection, else the top of that reel; blank when there is nothing of yours. */
export function currentPieces(pieces: FilmPiece[], picks?: Partial<Record<string, PickRef>>): Partial<Record<Slot, FilmPiece>> {
  const out: Partial<Record<Slot, FilmPiece>> = {};
  for (const s of SLOTS) {
    const opts = optionsFor(pieces, s);
    const pick = picks?.[s];
    // a typed card's id is its place among the post's parts, and routing can reorder those (activities audit):
    // hold on to the words the person chose, never to whatever now sits at that index
    const card = pick?.id.startsWith('card:') && pick.text ? norm(pick.text) : null;
    const found = !pick ? undefined : card !== null
      ? opts.find((o) => o.source === 'card' && o.postId === pick.postId && norm(o.text) === card) ?? opts.find((o) => o.id === pick.id && norm(o.text) === card)
      : opts.find((o) => o.id === pick.id);
    const top = found ?? opts[0];
    if (top) out[s] = top;
  }
  return out;
}

export function toPick(p: FilmPiece): PickRef {
  const pick: PickRef = { id: p.id, kind: p.source, refId: p.refId, text: p.text };
  if (p.img) pick.img = p.img;
  if (p.sheet) pick.sheet = true;
  if (p.postId) pick.postId = p.postId;
  return pick;
}

/** The picks the profile should hold for this row, or null when it already does. Every filled slot is written, so the backend never has to guess. */
export function picksToSave(current: Partial<Record<Slot, FilmPiece>>, have?: Partial<Record<string, PickRef>>): Record<string, PickRef> | null {
  const next: Record<string, PickRef> = {};
  for (const s of SLOTS) if (current[s]) next[s] = toPick(current[s]!);
  // the words too: a piece keeps its id when an exercise's routing reorders its parts, and the backend rolls from the words
  const same = SLOTS.every((s) => (next[s]?.id ?? null) === (have?.[s]?.id ?? null) && (next[s]?.text ?? null) === (have?.[s]?.text ?? null));
  return same ? null : next;
}

/** The film's state in plain words (owner, 2026-09-22: "2 of 3 to roll" is a weird way to say it): what it still needs, or that it is ready. */
export function filmKicker(cur: Partial<Record<Slot, FilmPiece>>): string {
  const gap = REQUIRED.find((s) => !cur[s]);
  if (!gap) return 'Your first film · ready to roll';
  if (!SLOTS.some((s) => cur[s])) return 'Your first film · nothing in it yet';
  return `Your first film · still needs ${SLOT_NOUN[gap]}`;
}

/** The row as one string, the same way the backend keys a poster to it (kindling-film.service pieceKey). */
export function pieceKey(cur: Partial<Record<Slot, FilmPiece>>): string {
  return SLOTS.map((s) => cur[s]?.id ?? '').join('|');
}

/**
 * The film so far, the instant a card lands: each piece as you wrote it, side
 * by side. It used to be a template ("Someone, at somewhere, wants …"), which
 * read badly the moment a piece was a sentence of its own (owner, 2026-09-22:
 * "wants Everyone has to attend dressed as a pirate"). The crew's pass
 * (sayFilmLine) re-says it as one sentence a moment later; this is what shows
 * until then, and whenever the crew is off.
 */
export type LoglineBit = { slot: Slot; text: string; filled: boolean; before?: string; after?: string };
export function logline(cur: Partial<Record<Slot, FilmPiece>>): LoglineBit[] {
  const out: LoglineBit[] = [];
  const add = (slot: Slot, blank: string | null, after?: string, open = '') => {
    const p = cur[slot];
    if (!p && blank === null) return;
    out.push({ slot, text: p?.text ?? blank!, filled: !!p, before: `${out.length ? ' · ' : ''}${open}`, after });
  };
  add('someone', 'someone');
  add('somewhere', 'somewhere');
  add('a want', 'a want');
  add('a look', null);
  add('a line', null, '”', '“');
  return out;
}

export const LINE_SLOTS = ['someone', 'somewhere', 'a want', 'a look'] as const;
/** What the crew's pass is given: the words on the table, the first line left out (it is a quote and goes on verbatim). */
export function lineInput(cur: Partial<Record<Slot, FilmPiece>>): Partial<Record<(typeof LINE_SLOTS)[number], string>> {
  const out: Partial<Record<(typeof LINE_SLOTS)[number], string>> = {};
  for (const s of LINE_SLOTS) { const t = cur[s]?.text?.trim(); if (t) out[s] = t.slice(0, 300); }
  return out;
}
